// src/pages/SocialMedia.jsx
import { motion } from "framer-motion";
import { Linkedin, Youtube, Instagram } from "lucide-react";
import newsHero from "../assets/socialmediahero.jpg";

export default function SocialMedia() {
  const socialLinks = [
    {
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/ajaya-conservation-trust/",
      description:
        "Follow our LinkedIn page for professional updates, NGO initiatives, and impact stories.",
      icon: <Linkedin className="w-8 h-8 text-blue-700" />,
      bg: "bg-blue-50",
    },
    {
      name: "YouTube",
      url: "https://www.youtube.com/@AjayaConservationTrust",
      description:
        "Watch our rescue stories, awareness campaigns, and interviews with volunteers on YouTube.",
      icon: <Youtube className="w-8 h-8 text-red-600" />,
      bg: "bg-red-50",
    },
    {
      name: "Instagram",
      url: "https://www.instagram.com/ajayaconservationtrust?igsh=MTEyOGhycmQ4MjJxcQ==",
      description:
        "Catch behind-the-scenes moments, event highlights, and inspiring wildlife images.",
      icon: <Instagram className="w-8 h-8 text-pink-500" />,
      bg: "bg-pink-50",
    },
  ];

  return (
    <div className="bg-white  min-h-screen">
      {/* ================= HERO ================= */}

<section
  className="relative min-h-[55vh] flex items-center justify-center overflow-hidden bg-cover bg-center"
  style={{
    backgroundImage: `url(${newsHero})`,
  }}
>
  {/* Background Overlay */}
  <div className="absolute inset-0 bg-black/60"></div>

  {/* Decorative circles */}
  <div className="absolute top-20 left-10 w-40 h-40 bg-lime-400/10 rounded-full blur-3xl"></div>

  <div className="absolute bottom-10 right-10 w-60 h-60 bg-lime-400/10 rounded-full blur-3xl"></div>

  {/* Hero Content */}
  <div className="relative z-10 text-center max-w-4xl mx-auto px-6">

    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="text-lime-400 uppercase tracking-[0.3em] text-sm font-semibold mb-5"
    >
      Stay Connected With ACoT
    </motion.p>

    <motion.h1
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="text-5xl md:text-7xl font-bold mb-6"
    >
      SOCIAL <span className="text-lime-400">MEDIA</span>
    </motion.h1>

    <motion.p
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 1,
        delay: 0.2,
      }}
      className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
    >
      Follow our journey, discover our conservation initiatives,
      and stay connected with the work of Ajaya Conservation Trust.
    </motion.p>

  </div>
</section>
{/* Embedded Feeds */}
      <section className="bg-gray-100 text-gray-900 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-8">
            Live Feeds & Highlights
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {/* YouTube Embed */}
            <div className="rounded-xl overflow-hidden shadow-lg bg-white p-4">
              <h3 className="text-xl font-semibold mb-4">Latest YouTube Video</h3>
              <iframe
                className="w-full h-64 rounded-lg"
                src="https://www.youtube.com/@AjayaConservationTrust"
                title="YouTube video"
                frameBorder="0"
                allowFullScreen
              ></iframe>
            </div>

            {/* Instagram Embed */}
            <div className="rounded-xl overflow-hidden shadow-lg bg-white p-4">
              <h3 className="text-xl font-semibold mb-4">Instagram Highlights</h3>
              <iframe
                className="w-full h-64 rounded-lg"
                src="https://www.instagram.com/ajayaconservationtrust?igsh=MTEyOGhycmQ4MjJxcQ=="
                title="Instagram post"
                frameBorder="0"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      </section>
      {/* Social Links Section */}
      <section className="max-w-6xl mx-auto py-16 px-6 text-gray-900">
        <h2 className="text-3xl font-bold mb-12 text-center">
          Follow Our Journey 🌍
        </h2>
        <div className="grid md:grid-cols-3 gap-10">
          {socialLinks.map((social, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              className={`${social.bg} p-8 rounded-2xl shadow-lg flex flex-col items-center text-center`}
            >
              {social.icon}
              <h3 className="mt-4 text-xl font-semibold">{social.name}</h3>
              <p className="mt-2 text-gray-600">{social.description}</p>
              <a
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-block bg-lime-600 text-white px-6 py-3 rounded-full font-bold hover:bg-lime-700 transition"
              >
                Visit {social.name}
              </a>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-lime-600 text-center text-white">
        <h2 className="text-3xl font-bold mb-4">Let’s Stay Connected ✨</h2>
        <p className="mb-8 max-w-2xl mx-auto">
          Your engagement helps spread awareness and build stronger communities.
          Join us online and amplify our mission.
        </p>
        <motion.a
          href="/donate"
          whileHover={{ scale: 1.1 }}
          className="bg-white text-lime-600 font-bold px-8 py-4 rounded-full shadow-lg"
        >
          Donate Now
        </motion.a>
      </section>
    </div>
  );
}
